/**
 * Parse a FastAPI error body into:
 *   - `message`: a single human-readable sentence suitable for toasts/banners
 *   - `fields`:  the Set of field names (from `loc`) that failed validation,
 *                so forms can highlight the offending inputs.
 *
 * FastAPI shapes handled:
 *  - 422 validation → `{ detail: [{ loc: [...], msg: "...", type: "..." }] }`
 *  - HTTPException → `{ detail: "string message" }`
 */
export function parseError(body, fallback = "Something went wrong.") {
  const detail = body && body.detail;
  const fields = new Set();
  if (!detail) return { message: fallback, fields };
  if (typeof detail === "string") return { message: detail, fields };
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item.msg === "string") {
          const field = Array.isArray(item.loc)
            ? item.loc.filter((l) => l !== "body").join(".")
            : "";
          if (field) fields.add(field);
          // Strip Pydantic v2's "Value error, " prefix so the message reads
          // like a normal sentence to end users.
          const msg = item.msg.replace(/^Value error, /, "");
          return field ? `${field}: ${msg}` : msg;
        }
        return null;
      })
      .filter(Boolean);
    if (messages.length) return { message: messages.join(" · "), fields };
  }
  return { message: fallback, fields };
}

/** Convenience alias — returns just the message. */
export function readableError(body, fallback = "Something went wrong.") {
  return parseError(body, fallback).message;
}
